/**
 * Card helpers shared by every dashboard.
 *
 * The readers were written for the School Network dashboard (networkData.js)
 * and are generic: they only read the Data Map card shapes (KPICard,
 * TableCard, MovementCard …), never a dashboard-specific field. Partner,
 * Individual and Class import them from here so there is one implementation.
 */
import {findSection, findCard, cellText, formatCell, rowsFrom, distinct, rowMatches, orgColumn, addInfoOf, titleCase, whole, benchmarkOf} from '../NetworkDashboard/networkData';
import {
    SUPPRESSION_MIN_N, SUPPRESSED_COPY,
    isSuppressedCell, isSuppressedItem, isSuppressedKpi, isSuppressedBenchmark, isSuppressedCycle, isScopeSuppressed, scopeN,
} from '../../utils/suppression';

export {findSection, findCard, cellText, formatCell, rowsFrom, distinct, rowMatches, orgColumn, addInfoOf, titleCase, whole, benchmarkOf};
export {SUPPRESSION_MIN_N, SUPPRESSED_COPY, isSuppressedCell, isSuppressedItem, isSuppressedKpi, isSuppressedBenchmark, isSuppressedCycle, isScopeSuppressed, scopeN};

const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

/** A KPICard's value as shown on a tile: whole number (+ % when asked), text as given, "—" when absent or suppressed. */
export const kpiText = (card, pct = false) => {
    if (!card || isSuppressedKpi(card)) return '—';
    const v = card.kpi_value;
    if (pct && isNum(v)) return `${Math.trunc(Number(v))}%`;
    return cellText(v);
};

/** The tile's sub-line for a suppressed KPI (the suppression copy), else null. */
export const kpiNote = (card) => (isSuppressedKpi(card) ? SUPPRESSED_COPY : null);

/** True when any of the given cards is flagged "Preview only" (reassessment not completed). */
export const anyPreview = (...cards) => cards.some((c) => c?.preview_only === true);

/**
 * One side of a MovementCard (baseline / reassess) for a two-year score tile:
 * { label, value, band, global, suppressed } — "—" / null where the cycle has no
 * value yet or is under the n floor (suppressed).
 */
export const movementSide = (card, side) => {
    const cycle = card?.[side] || {};
    const pct = card?.unit === 'pp' ? '%' : '';
    const g = (card?.benchmarks || []).find((b) => /global/i.test(b?.name || ''));
    const suppressed = isSuppressedCycle(cycle);
    const gSuppressed = !!g && (g.suppressed === true || g[`suppressed_${side}`] === true);
    return {
        label: cycle.label || (side === 'baseline' ? 'Baseline' : 'Reassessment'),
        value: !suppressed && isNum(cycle.value) ? `${Math.trunc(Number(cycle.value))}${pct}` : '—',
        band: suppressed ? null : (cycle.band || null),
        global: !gSuppressed && isNum(g?.[side]) ? `${Math.trunc(Number(g[side]))}${pct}` : '—',
        suppressed,
    };
};

/** A MovementCard's delta as "+7 pts" / "-7 %p", "Pending" before reassessment, "—" when a side is suppressed. */
export const movementDelta = (card) => {
    if (isSuppressedCycle(card?.baseline) || isSuppressedCycle(card?.reassess)) return '—';
    const base = card?.baseline?.value;
    const re = card?.reassess?.value;
    const delta = isNum(card?.delta) ? Number(card.delta) : (isNum(base) && isNum(re) ? Number(re) - Number(base) : null);
    if (delta === null) return 'Pending';
    const unit = card?.unit === 'pp' ? '%p' : (card?.unit || '');
    const d = Math.trunc(delta);
    return `${d >= 0 ? '+' : ''}${d} ${unit}`.trim();
};
